import useSWR, { mutate } from "swr";
import { comparisonsService } from "../../api_client";
import type { CreateComparisonDto } from "../../../api";
import type { SavedComparisonDto, CreateComparisonRequest } from "../data/comparison.types";
import { COMPARISONS_SWR_KEY } from "./useMyComparisons";
import { savedComparisonKey } from "./useComparisonLookup";

/** Fetch a single saved comparison by id (includes cached AI recommendation). */
export function useSavedComparison(id: string | null | undefined) {
  const swr = useSWR<SavedComparisonDto>(
    id ? savedComparisonKey(id) : null,
    async () => {
      const res = await comparisonsService.getComparison(id as string);
      return res.data as unknown as SavedComparisonDto;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  return {
    data: swr.data,
    isLoading: Boolean(id) && (swr.isLoading ?? (!swr.data && !swr.error)),
    error: swr.error,
    refresh: swr.mutate,
  };
}

/**
 * Revalidate every cached "My Comparisons" page. Failures are logged, never
 * rethrown — the mutation they follow has already succeeded.
 */
function invalidateComparisons(): Promise<unknown> {
  return mutate(
    (key) => typeof key === "string" && key.startsWith(COMPARISONS_SWR_KEY),
  ).catch((err) => {
    console.error("Failed to invalidate comparisons cache", err);
  });
}

export async function createComparison(
  payload: CreateComparisonRequest,
): Promise<SavedComparisonDto> {
  const res = await comparisonsService.createComparison(
    payload as unknown as CreateComparisonDto,
  );
  // Invalidate every cached "My Comparisons" page (all page/search/sort
  // variants share this key prefix) so the new comparison appears immediately.
  // Non-fatal — the save already succeeded, so a cache-invalidation hiccup must
  // never surface as a failed save. try/catch would not catch this: mutate is
  // async, so a rejection escapes as an unhandled rejection rather than being
  // caught. Attach the handler to the promise instead.
  void invalidateComparisons();
  return res.data as unknown as SavedComparisonDto;
}

export async function deleteComparison(id: string): Promise<void> {
  await comparisonsService.deleteComparison(id);
  void invalidateComparisons();
}
